"use client";
import React, { useEffect, useState, useRef } from "react";

const AnalyticsTracker = ({
	apiUrl = "https://api.getcreatr.com/webapp-tracking",
}) => {
	const [sessionId, setSessionId] = useState<string | null>(null);

	// Use the current host as the siteKey
	const siteKey = React.useMemo(() => (typeof window !== 'undefined' ? window.location.host : ''), []);

	const shouldTrackAnalytics = () => {
		const hostname = window.location.hostname;
		const isProdUrl = hostname.endsWith('getcreatr.app');
		const isLocalhost = hostname.includes('localhost') || hostname.includes('127.0.0.1');

		return isProdUrl && !isLocalhost;
	};

	// Initialize sessionId on the client side only
	useEffect(() => {
		const initializeSession = () => {
			// Try to get existing session ID from localStorage
			let storedSessionId = localStorage.getItem("analyticsSessionId");

			// Check for existing session cookie
			const getCookie = (name: string): string | undefined => {
				const value = `; ${document.cookie}`;
				const parts = value.split(`; ${name}=`);
				if (parts.length === 2) return parts.pop()?.split(";").shift();
			};

			const cookieSessionId = getCookie("analyticsSessionId");

			// Use existing session ID or create new one
			if (!storedSessionId && !cookieSessionId) {
				// Generate new session ID only if none exists
				const newSessionId = `session_${Math.random().toString(36).substring(2)}_${Date.now()}`;

				// Store in both localStorage and cookie
				try {
					localStorage.setItem("analyticsSessionId", newSessionId);
					// Set cookie with 30-day expiry
					document.cookie = `analyticsSessionId=${newSessionId};domain=.getcreatr.app;path=/;max-age=2592000;SameSite=Lax`;
					storedSessionId = newSessionId;
				} catch (e) {
					console.error("Error storing session ID:", e);
				}
			}

			// Prefer localStorage version, fall back to cookie version
			setSessionId((storedSessionId as string) || (cookieSessionId as string) || null);
		};

		initializeSession();
	}, []);

	// Track the accumulated time
	const accumulatedTimeRef = useRef(0);
	const lastActivityRef = useRef(Date.now());
	const isActiveRef = useRef(true);
	const currentPathRef = useRef(
		typeof window !== "undefined" ? window.location.pathname : "",
	);

	// Track page view
	const trackPageView = async () => {
		if (!sessionId || !shouldTrackAnalytics()) return;

		try {
			await fetch(`${apiUrl}/track`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Site-Key": siteKey,
				},
				body: JSON.stringify({
					pageUrl: window.location.pathname,
					sessionId,
					referrer: document.referrer || "direct",
					timestamp: new Date().toISOString(),
					timeSpent: 0,
					isNewPageView: true,
					userAgent: navigator.userAgent,
					screenResolution: `${window.screen.width}x${window.screen.height}`,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				}),
			});
		} catch (error) {
			console.error("Track request failed:", error);
		}
	};

	// Track accumulated time
	const trackTimeSpent = async () => {
		if (!isActiveRef.current || !sessionId || !shouldTrackAnalytics()) return;

		const now = Date.now();
		const timeElapsed = Math.floor((now - lastActivityRef.current) / 1000);

		if (timeElapsed > 0) {
			accumulatedTimeRef.current += timeElapsed;
			lastActivityRef.current = now;

			try {
				await fetch(`${apiUrl}/track`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Site-Key": siteKey,
					},
					body: JSON.stringify({
						pageUrl: window.location.pathname,
						sessionId,
						referrer: document.referrer || "direct",
						timestamp: new Date().toISOString(),
						timeSpent: timeElapsed,
						isNewPageView: false,
						userAgent: navigator.userAgent,
						screenResolution: `${window.screen.width}x${window.screen.height}`,
						timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					}),
				});
			} catch (error) {
				console.error("Error tracking time:", error);
			}
		}
	};

	// Effect for path change detection
	useEffect(() => {
		if (!sessionId) return;

		const handlePathChange = () => {
			const newPath = window.location.pathname;
			if (newPath !== currentPathRef.current) {
				// Send final time tracking for previous page
				trackTimeSpent();

				// Reset time tracking for new page
				accumulatedTimeRef.current = 0;
				lastActivityRef.current = Date.now();

				// Update path and track new page view
				currentPathRef.current = newPath;
				trackPageView();
			}
		};

		// Track initial page view
		trackPageView();

		// Listen for route changes
		window.addEventListener("popstate", handlePathChange);

		// For SPA route changes
		const originalPushState = window.history.pushState;
		window.history.pushState = function (...args) {
			originalPushState.apply(this, args as any);
			handlePathChange();
		};

		return () => {
			window.removeEventListener("popstate", handlePathChange);
			window.history.pushState = originalPushState;
			// Send final tracking before unmount
			trackTimeSpent();
		};
	}, [sessionId]); // Add sessionId as dependency

	// Effect for time tracking
	useEffect(() => {
		if (!sessionId) return;

		// Activity tracking
		const handleActivity = () => {
			if (!isActiveRef.current) {
				// If becoming active again, reset the last activity time
				lastActivityRef.current = Date.now();
			}
			isActiveRef.current = true;
		};

		// Inactivity tracking
		const handleInactivity = () => {
			if (isActiveRef.current) {
				// Send the accumulated time before going inactive
				trackTimeSpent();
				isActiveRef.current = false;
			}
		};

		// Visibility change handling
		const handleVisibilityChange = () => {
			if (document.hidden) {
				handleInactivity();
			} else {
				handleActivity();
			}
		};

		// Set up time tracking interval
		const timeTrackingInterval: ReturnType<typeof setTimeout> = setInterval(() => {
			if (isActiveRef.current) {
				trackTimeSpent();
			}
		}, 5000); // Track every 5 seconds

		// Set up inactivity timeout
		let inactivityTimeout: ReturnType<typeof setTimeout>;
		const resetInactivityTimeout = () => {
			clearTimeout(inactivityTimeout);
			handleActivity();
			inactivityTimeout = setTimeout(handleInactivity, 60000); // 1 minute of inactivity
		};

		// Activity event listeners
		const events = ["mousemove", "keypress", "scroll", "click", "touchstart"];
		events.forEach((event) => {
			window.addEventListener(event, resetInactivityTimeout);
		});

		// Visibility change listener
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// Initial timeout
		resetInactivityTimeout();

		// Cleanup
		return () => {
			clearInterval(timeTrackingInterval);
			clearTimeout(inactivityTimeout);
			events.forEach((event) => {
				window.removeEventListener(event, resetInactivityTimeout);
			});
			document.removeEventListener("visibilitychange", handleVisibilityChange);

			// Final tracking before unmount
			if (isActiveRef.current) {
				trackTimeSpent();
			}
		};
	}, [sessionId, apiUrl]); // Dependencies

	return null;
};

export default AnalyticsTracker;
